import React, { useState } from 'react';
import { Send, Mail, MapPin, Music, CheckCircle, ExternalLink } from 'lucide-react';
import Layout from '../components/Layout';
import { siteContent } from '../data/siteContent';
import { useTranslation } from '../context/TranslationContext';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../data/seoMeta';

function ContactPage() {
  var { t } = useTranslation();
  var [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  var [isSubmitting, setIsSubmitting] = useState(false);
  var [isSubmitted, setIsSubmitted] = useState(false);
  var [submitError, setSubmitError] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    var API_URL = process.env.REACT_APP_BACKEND_URL;
    
    fetch(API_URL + '/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
      .then(function(response) {
        return response.text().then(function(text) {
          var data;
          try { data = JSON.parse(text); } catch(e) { data = {}; }
          if (!response.ok) {
            throw new Error(data.detail || 'Failed to send message');
          }
          return data;
        });
      })
      .then(function() {
        setIsSubmitting(false);
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      })
      .catch(function(err) {
        setIsSubmitting(false);
        setSubmitError(err.message || 'Something went wrong. Please try again.');
      });
  }

  function handleChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  return (
    <Layout>
      <SEO title={PAGE_SEO['contact'].title} description={PAGE_SEO['contact'].description} url="/contact" />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-neutral-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-4">{t('contactGetInTouch')}</p>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            {t('contactTitle')}
          </h1>
          <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
            {t('contactSubtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-section" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('contactLetsConnect')}
                </h2>
                <p className="text-neutral-400 leading-relaxed">
                  {t('contactDescription')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-neutral-900/50">
                  <div className="w-10 h-10 rounded-lg bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{t('contactEmail')}</h4>
                    <p className="text-neutral-500 text-sm">{siteContent.contact.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-neutral-900/50">
                  <div className="w-10 h-10 rounded-lg bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{t('contactLocation')}</h4>
                    <p className="text-neutral-500 text-sm">{siteContent.contact.location}</p>
                    <p className="text-neutral-600 text-xs mt-1">{siteContent.contact.shipping}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-neutral-900/50">
                  <div className="w-10 h-10 rounded-lg bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">LAJO</h4>
                    <a
                      href={siteContent.links.lajo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#d4af37] text-sm hover:underline flex items-center gap-1"
                    >
                      Latin American Jazz Orchestra
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Profile Card */}
              <div className="p-6 rounded-2xl bg-neutral-900/50 border border-white/5">
                <img
                  src="https://customer-assets.emergentagent.com/job_0a4da157-faa7-4b5b-9335-9e1281623bd2/artifacts/9mfir2v7_IMG_8870.JPG"
                  alt="Isaías Jiménez"
                  data-testid="contact-profile-photo"
                  className="w-full aspect-square rounded-2xl object-cover mb-4"
                  referrerPolicy="no-referrer"
                />
                <h4 className="text-white font-semibold">Isaías Jiménez</h4>
                <p className="text-sm text-neutral-600 mt-1">Musician | Inventor | Arranger</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="p-8 md:p-12 rounded-2xl bg-neutral-900/30 border border-white/5">
                {isSubmitted ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-[#d4af37]" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">{t('contactSent')}</h3>
                    <p className="text-neutral-400 mb-8">
                      {t('contactThankYou')}
                    </p>
                    <button
                      onClick={function() { setIsSubmitted(false); }}
                      className="text-[#d4af37] hover:underline"
                    >
                      {t('contactSendAnother')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h3 className="text-2xl font-bold text-white mb-8">{t('contactSendMessage')}</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-sm text-neutral-500 mb-2">
                          {t('contactName')} *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="contact-name-input"
                          className="w-full px-5 py-4 bg-black border border-white/10 rounded-xl text-white placeholder:text-neutral-700 focus:outline-none focus:border-[#d4af37]/50"
                          placeholder={t('contactYourName')}
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm text-neutral-500 mb-2">
                          {t('contactEmail')} *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="contact-email-input"
                          className="w-full px-5 py-4 bg-black border border-white/10 rounded-xl text-white placeholder:text-neutral-700 focus:outline-none focus:border-[#d4af37]/50"
                          placeholder={t('contactYourEmail')}
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="subject" className="block text-sm text-neutral-500 mb-2">
                        {t('contactSubject')} *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        data-testid="contact-subject-input"
                        className="w-full px-5 py-4 bg-black border border-white/10 rounded-xl text-white placeholder:text-neutral-700 focus:outline-none focus:border-[#d4af37]/50"
                        placeholder={t('contactSubjectPlaceholder')}
                      />
                    </div>

                    <div className="mb-8">
                      <label htmlFor="message" className="block text-sm text-neutral-500 mb-2">
                        {t('contactMessage')} *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        data-testid="contact-message-input"
                        className="w-full px-5 py-4 bg-black border border-white/10 rounded-xl text-white placeholder:text-neutral-700 focus:outline-none focus:border-[#d4af37]/50 resize-none"
                        placeholder={t('contactMessagePlaceholder')}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-button"
                      className="w-full flex items-center justify-center gap-3 bg-[#d4af37] text-black hover:bg-white disabled:bg-neutral-800 disabled:text-neutral-600 rounded-full px-8 py-5 font-semibold text-lg transition-all duration-300 hover:scale-[1.02] disabled:scale-100"
                    >
                      {isSubmitting ? (
                        <span>{t('contactSending')}</span>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {t('contactSend')}
                        </>
                      )}
                    </button>

                    {submitError && (
                      <div data-testid="contact-error" className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
                        {submitError}
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ContactPage;
